import { getScreenName } from '@/libs/analytics/track-page-nav';
import { MediaGuideMetadata } from '@/utils/media/types';
import {
    MediaUpdateGuide,
    MediaUpdateGuideProgressActionEnum,
    WorkflowWorkflowType,
} from '@calm/evexia-web-sdk';
import { useGuidePlayer } from '@rhg/hooks/use-guide-player';
import createMediaV2Api from '@rhg/libs/api/create-media-v2-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    invalidateQueryKey,
    queryClient,
    useMutation,
} from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { getLocalDateTimeString } from '@rhg/utils/date-time';
import { MediaEvent } from '@rhg/utils/media';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useActiveCircle } from '../use-active-circle';
import { guidesQueryOptions, invalidateGuides } from '../use-guides';
import {
    CachedGuideFields,
    invalidateMultiGuideQueries,
} from '../use-guides/cache';
import {
    workflowByDefnIdForRecipientQueryOptions,
    workflowByTypeForRecipientQueryOptions,
} from '../use-workflows';

function getProgressAction(
    eventType: MediaEvent
): MediaUpdateGuideProgressActionEnum | undefined {
    switch (eventType) {
        case MediaEvent.Play:
            return 'play';
        case MediaEvent.Pause:
            return 'pause';
        case MediaEvent.Seeked:
            return 'seek';
        case MediaEvent.TimeUpdate:
            return 'complete_90';
        case MediaEvent.Ended:
            return 'complete';
        default:
            return undefined;
    }
}

function getCachedGuideFields(
    request: MediaUpdateGuide
): CachedGuideFields | undefined {
    const fields: CachedGuideFields = {};
    if (typeof request.isFavorite !== 'undefined') {
        fields.favorite = request.isFavorite;
    }
    if (
        request.progressAction === 'complete' ||
        request.progressAction === 'complete_90'
    ) {
        fields.completed = true;
    }
    if (
        (request.progressAction === 'start' ||
            request.progressAction === 'play') &&
        request.timestamp
    ) {
        fields.lastPlayedAt = request.timestamp;
    }
    return Object.keys(fields).length > 0 ? fields : undefined;
}

interface UpdateGuideParameters {
    guideId: string;
    workflowDefnId?: string;
    workflowType?: WorkflowWorkflowType;
    request: MediaUpdateGuide;
}
export function useUpdateGuide() {
    const { getAccessTokenSilently } = useAuth();
    const { activeCircle, hasPermission } = useActiveCircle();
    const rHGApp = getRhgAppHeader();
    const activeCircleId = activeCircle?.recipient.id;

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            guideId = '',
            request,
        }: UpdateGuideParameters) => {
            const api = createMediaV2Api(getAccessTokenSilently);
            if (
                hasPermission('media', 'u') &&
                hasPermission('clinical_program', 'u')
            ) {
                const response = await api.v2UpdateGuide({
                    circleAuthorizationToken: activeCircle!.authorizationJWT!,
                    recipientId: activeCircle!.recipient.id,
                    guideId,
                    mediaUpdateGuide: request,
                    rHGApp,
                });
                return response;
            } else {
                throw new Error('Update Guide Permission Denied');
            }
        },
        onMutate: ({ guideId, request }: UpdateGuideParameters) => {
            if (!activeCircleId) {
                return;
            }
            // Snapshot the previous value
            const key = guidesQueryOptions.queryKey({
                guideIds: new Set([guideId]),
                circleId: activeCircleId,
            });
            const previous =
                queryClient.getQueryData<MediaGuideMetadata[]>(key);

            if (previous) {
                queryClient.setQueryData<MediaGuideMetadata[]>(
                    key,
                    (previousGuides) => {
                        const previousGuide = previousGuides?.[0];
                        if (!previousGuide) {
                            return previous;
                        }
                        const updates: Partial<MediaGuideMetadata> = {};
                        if (typeof request.progressDurationMs !== 'undefined') {
                            updates.progress = request.progressDurationMs;
                        }
                        if (typeof request.isFavorite !== 'undefined') {
                            updates.favorite = request.isFavorite;
                        }
                        if (
                            request.isFavorite &&
                            typeof request.timestamp !== 'undefined'
                        ) {
                            updates.favoritedAt = request.timestamp;
                        }
                        const updatedGuide: MediaGuideMetadata = {
                            ...previousGuide,
                            ...updates,
                        };
                        return [updatedGuide];
                    }
                );
            }
            return { previous };
        },
        onError: (err, { guideId }, context) => {
            if (!activeCircleId || !context?.previous) {
                return;
            }
            const key = guidesQueryOptions.queryKey({
                guideIds: new Set([guideId]),
                circleId: activeCircleId,
            });
            queryClient.setQueryData(key, context.previous);
        },
        onSuccess: async (
            _,
            { guideId, request, workflowDefnId = '', workflowType = undefined }
        ) => {
            const cachedFields = getCachedGuideFields(request);
            if (activeCircleId && cachedFields) {
                // onMutate only writes favorite, favoritedAt and progress, so the confirmed
                // completed and lastPlayedAt must land before multi-guide queries rebuild from it.
                const key = guidesQueryOptions.queryKey({
                    guideIds: new Set([guideId]),
                    circleId: activeCircleId,
                });
                if (queryClient.getQueryData(key)) {
                    queryClient.setQueryData<MediaGuideMetadata[]>(
                        key,
                        (guides) =>
                            guides?.[0]
                                ? [{ ...guides[0], ...cachedFields }]
                                : guides
                    );
                }
            }
            if (activeCircleId) {
                await Promise.all([
                    cachedFields
                        ? invalidateMultiGuideQueries({
                              circleId: activeCircleId,
                              guideId,
                          })
                        : undefined,
                    invalidateGuides({
                        guideIds: new Set([guideId]),
                        circleId: activeCircleId,
                        skipGuides: true, // Skip guides as they are already updated in onMutate
                    }),
                    workflowDefnId
                        ? invalidateQueryKey(
                              workflowByDefnIdForRecipientQueryOptions.queryKey(
                                  {
                                      workflowDefnId,
                                      recipientId: activeCircleId,
                                  }
                              )
                          )
                        : undefined,
                    workflowType
                        ? invalidateQueryKey(
                              workflowByTypeForRecipientQueryOptions.queryKey({
                                  workflowType,
                                  recipientId: activeCircleId,
                              })
                          )
                        : undefined,
                ]);
            }
        },
    });
    return {
        updateGuide: mutate,
        updateGuideAsync: mutateAsync,
        ...otherProps,
    };
}

type UpdateGuideProgressParameters = Omit<UpdateGuideParameters, 'request'> & {
    progressDurationMs: number;
    eventType: MediaEvent;
};
export function useUpdateGuideProgress() {
    const { updateGuideAsync } = useUpdateGuide();
    const progressSource = useGuidePlayer()?.progressSource;
    const { isAuthenticated } = useAuth();
    const { pathname, query } = useRouter();
    const screenName = useMemo(
        () =>
            getScreenName(
                { pathname, query },
                { IsAuthenticated: isAuthenticated }
            ),
        [pathname, query, isAuthenticated]
    );

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            workflowDefnId,
            workflowType,
            guideId,
            progressDurationMs,
            eventType,
        }: UpdateGuideProgressParameters) =>
            updateGuideAsync({
                guideId,
                workflowDefnId,
                workflowType,
                request: {
                    progressDurationMs: Math.floor(progressDurationMs),
                    timestamp: getLocalDateTimeString(),
                    progressAction: getProgressAction(eventType),
                    // A surface such as the emotions check-in results can pin
                    // the session to itself (e.g. `{ checkInId }`).
                    source: progressSource?.source ?? screenName,
                    sourceAttributes: progressSource?.sourceAttributes,
                },
            }),
    });

    return {
        updateGuideProgress: mutate,
        updateGuideProgressAsync: mutateAsync,
        ...otherProps,
    };
}
